import React, { useState, useEffect } from 'react';
import {
  Calendar,
  ExternalLink,
  Filter,
  ChevronDown,
  MessageCircle,
  Heart,
  Share2,
  TrendingUp,
  Users,
  Eye,
  Search,
  ArrowUpDown
} from 'lucide-react';
import { buildApiUrl } from '../config';
import { getPolicyTagIcon, getPolicyTagColor, getAllPolicyTagsSync } from '../scripts/bills/policyTagUtils';
import { getSocialLinkIcon, XBrandIcon } from '../components/SocialLinkIcons';

const SocialMediaPosts = ({ politician, isDarkMode }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [platformFilter, setPlatformFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [tagFilter, setTagFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' for newest first, 'asc' for oldest first
  const [expandedPost, setExpandedPost] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  // Sample data removed - now showing empty state when no posts exist

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        console.log(`🔍 Fetching social media posts for: "${politician.name}"`);
        
        // Try to fetch real social media posts from the politician's data
        const response = await fetch(buildApiUrl(`/api/politician-social-media/${encodeURIComponent(politician.name)}`));
        
        if (response.ok) {
          const data = await response.json();
          console.log(`✅ Successfully loaded social media posts for: "${politician.name}" with ${data.length || 0} posts`);
          
          // Debug: Log post data including equivalent posts
          if (data && data.length > 0) {
            data.forEach((post, index) => {
              console.log(`📸 Post ${index + 1} data:`, {
                hasImage: post.hasImage,
                imageUrl: post.imageUrl,
                platform: post.platform,
                id: post.id,
                equivalentPosts: post.equivalentPosts || post.equivalent_posts,
                hasEquivalentPosts: !!(post.equivalentPosts || post.equivalent_posts),
                equivalentPostsLength: (post.equivalentPosts || post.equivalent_posts || []).length
              });
            });
          }
          
          setPosts(data || []); // Use empty array if no data
        } else if (response.status === 404) {
          console.log(`⚠️ No social media posts found for "${politician.name}"`);
          setPosts([]); // Show empty state
          setError(null); // Clear any previous errors
        } else {
          throw new Error(`Failed to fetch: ${response.status}`);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching social media posts:', err);
        setError('Failed to load social media posts');
        setPosts([]); // Show empty state on error
        setLoading(false);
      }
    };

    if (politician) {
      fetchPosts();
    }
  }, [politician]);

  // Normalize backend/platform values to UI keys
  const normalizePlatformToUi = (p) => {
    const v = (p || '').toLowerCase();
    if (v === 'x' || v === 'twitter') return 'twitter';
    if (v === 'fb') return 'facebook';
    return v;
  };

  const getPlatformIcon = (platform, size = "w-5 h-5", color = "") => {
    const IconComponent = getSocialLinkIcon(platform);
    return <IconComponent className={`${size} ${color}`.trim()} />;
  };

  const getPlatformColor = (platform) => {
    const p = normalizePlatformToUi(platform);
    switch (p) {
      case 'twitter':
        return isDarkMode ? 'text-blue-300 bg-[#1e3a8a]/30' : 'text-[#1e3a8a] bg-[#1e3a8a]/10';
      case 'facebook':
        return isDarkMode ? 'text-blue-500 bg-blue-900/20' : 'text-blue-700 bg-blue-100';
      case 'instagram':
        return isDarkMode ? 'text-pink-400 bg-pink-900/20' : 'text-pink-600 bg-pink-100';
      case 'youtube':
        return isDarkMode ? 'text-red-400 bg-red-900/20' : 'text-red-600 bg-red-100';
      default:
        return isDarkMode ? 'text-gray-400 bg-gray-800' : 'text-gray-600 bg-gray-100';
    }
  };

  const getPlatformShareColor = (platform) => {
    const p = normalizePlatformToUi(platform);
    switch (p) {
      case 'twitter':
        return isDarkMode 
          ? 'bg-[#1e3a8a] hover:bg-[#1e40af] text-white' 
          : 'bg-[#1e3a8a] hover:bg-[#1e40af] text-white';
      case 'facebook':
        return isDarkMode 
          ? 'bg-[#1877F2] hover:bg-[#166FE5] text-white' 
          : 'bg-[#1877F2] hover:bg-[#166FE5] text-white';
      case 'instagram':
        return isDarkMode 
          ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90 text-white' 
          : 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90 text-white';
      case 'youtube':
        return isDarkMode 
          ? 'bg-red-600 hover:bg-red-700 text-white' 
          : 'bg-red-600 hover:bg-red-700 text-white';
      default:
        return isDarkMode 
          ? 'bg-gray-600 hover:bg-gray-700 text-white' 
          : 'bg-gray-600 hover:bg-gray-700 text-white';
    }
  };

  const getPlatformDisplayName = (platform) => {
    const p = normalizePlatformToUi(platform);
    if (p === 'twitter') {
      return 'X';
    }
    return p.charAt(0).toUpperCase() + p.slice(1);
  };

  const getEngagementIcon = (platform, engagementType) => {
    switch (engagementType) {
      case 'likes':
        return <Heart className="w-4 h-4" />;
      case 'retweets':
      case 'shares':
        return <Share2 className="w-4 h-4" />;
      case 'comments':
        return <MessageCircle className="w-4 h-4" />;
      case 'views':
        return <Eye className="w-4 h-4" />;
      default:
        return <TrendingUp className="w-4 h-4" />;
    }
  };

  const formatEngagementNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const availablePlatforms = [
    { value: 'all', label: 'All Platforms' },
    { value: 'twitter', label: 'X' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' }
  ];

  const allPolicyTags = getAllPolicyTagsSync();

  const filteredPosts = posts.filter(post => {
    // Platform filter
    if (platformFilter !== 'all') {
      const matchesPlatform = normalizePlatformToUi(post.platform) === platformFilter;
      const equivalentPosts = post.equivalentPosts || post.equivalent_posts || [];
      const matchesEquivalentPlatform = equivalentPosts.some(eqPost => normalizePlatformToUi(eqPost.platform) === platformFilter);
      if (!matchesPlatform && !matchesEquivalentPlatform) return false;
    }
    
    // Search query filter (matches against caption/content)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const content = (post.content || '').toLowerCase();
      if (!content.includes(query)) return false;
    }
    
    // Tag filter
    if (tagFilter !== 'all') {
      const postTags = post.tags || [];
      if (!postTags.includes(tagFilter)) return false;
    }
    
    return true;
  }).sort((a, b) => {
    // Handle posts with no date - always put them at the bottom
    const aHasDate = a.date && a.date.trim() !== '';
    const bHasDate = b.date && b.date.trim() !== '';
    
    // If one has date and one doesn't, the one without date goes to bottom
    if (aHasDate && !bHasDate) return -1;
    if (!aHasDate && bHasDate) return 1;
    
    // If neither has date, maintain original order
    if (!aHasDate && !bHasDate) return 0;
    
    // Both have dates - sort by date
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    
    if (sortOrder === 'desc') {
      return dateB - dateA; // Newest first
    } else {
      return dateA - dateB; // Oldest first
    }
  });

  // Show empty state when there are no posts at all
  if (posts.length === 0 && !loading && !error) {
    return (
      <div className="space-y-4">
        {/* Empty State */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-12 border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} text-center`}>
          <div className="max-w-md mx-auto">
            <div className={`w-16 h-16 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
              <MessageCircle className={`w-8 h-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            </div>
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
              No Social Media Posts Yet
            </h3>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
              Social media posts from {politician.name} will appear here once they're available.
            </p>
            <div className={`inline-flex items-center px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <XBrandIcon className={`w-5 h-5 mr-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Posts will be visible across all platforms
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading social media posts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className={`w-12 h-12 ${isDarkMode ? 'text-gray-700' : 'text-gray-300'} mx-auto mb-4`}>
            <MessageCircle className="w-12 h-12" />
          </div>
          <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>Unable to load posts</h3>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Filter Section */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 md:p-6 border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
            <Filter className="w-5 h-5 mr-2" />
            Filter Posts
          </h3>
          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {filteredPosts.length} posts
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search Input */}
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search content.."
              className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>
          
          {/* Platform Filter */}
          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
              isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            {availablePlatforms.map(platform => (
              <option key={platform.value} value={platform.value}>
                {platform.label}
              </option>
            ))}
          </select>
          
          {/* Tag Filter */}
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
              isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="all">All Tags</option>
            {allPolicyTags.map(tag => (
              <option key={tag} value={tag}>
                {tag}
              </option>
            ))}
          </select>
          
          {/* Sort Order */}
          <div className="relative">
            <ArrowUpDown className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>
      </div>

      {/* Posts List */}
      <div className="space-y-4">
        {filteredPosts.length === 0 ? (
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-8 border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} text-center`}>
            <MessageCircle className={`w-12 h-12 ${isDarkMode ? 'text-gray-700' : 'text-gray-300'} mx-auto mb-4`} />
            <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>No posts found</h3>
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
              No social media posts found for the selected platform.
            </p>
          </div>
        ) : (
          filteredPosts.map((post) => {
            // Normalize equivalent posts (handle both camelCase and snake_case)
            const equivalentPosts = post.equivalentPosts || post.equivalent_posts || [];
            const hasEquivalentPosts = equivalentPosts.length > 0;
            
            // Debug logging for image data
            if (post.platform === 'instagram') {
              console.log('🔍 Instagram post image check:', {
                id: post.id,
                hasImage: post.hasImage,
                imageUrl: post.imageUrl,
                image_url: post.image_url,
                allKeys: Object.keys(post).filter(k => k.toLowerCase().includes('image'))
              });
            }
            
            const originalImageUrl = post.imageUrl || post.image_url;
            const isInstagramUrl = originalImageUrl && (
              originalImageUrl.includes('instagram.com') || 
              originalImageUrl.includes('cdninstagram.com')
            );
            const imageSrc = originalImageUrl && (isInstagramUrl 
              ? `${buildApiUrl('/api/proxy-image')}?url=${encodeURIComponent(originalImageUrl)}`
              : originalImageUrl);
            
            // Collect all platforms for this post (main + equivalent)
            const allPlatforms = [
              { platform: normalizePlatformToUi(post.platform), url: post.url },
              ...equivalentPosts.map(ep => ({ ...ep, platform: normalizePlatformToUi(ep.platform) }))
            ];
            
            return (
            <div 
              key={post.id}
              className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} overflow-hidden transition-all duration-200 hover:shadow-lg`}
            >
              {/* Horizontal Card Layout */}
              <div className="flex flex-col md:flex-row">
                {/* Left Side: Platform, Date, Caption */}
                <div className="flex-1 p-4 md:p-6 flex flex-col">
                  {/* Username and Date Header */}
                  <div className="flex items-center space-x-3 mb-3">
                    {/* Show platform icons for all platforms (main + equivalent) */}
                    <div className="flex items-center gap-2">
                      {allPlatforms.map((platformData, idx) => (
                        <div 
                          key={idx}
                          className={`p-2 rounded-lg ${getPlatformColor(platformData.platform)}`}
                          title={getPlatformDisplayName(platformData.platform)}
                        >
                          {getPlatformIcon(platformData.platform)}
                        </div>
                      ))}
                    </div>
                    <div className="flex-1">
                      {/* Show username if available, otherwise show platform names */}
                      {post.username ? (
                        <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          @{post.username}
                        </div>
                      ) : (
                        <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {hasEquivalentPosts 
                            ? allPlatforms.map(p => getPlatformDisplayName(p.platform)).join(' • ')
                            : getPlatformDisplayName(post.platform)
                          }
                        </div>
                      )}
                      {post.formattedDate && (
                        <div className={`flex items-center space-x-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                          <Calendar className="w-4 h-4" />
                          <span>{post.formattedDate}</span>
                          {post.time && (
                            <>
                              <span>•</span>
                              <span>{post.time}</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Caption/Content */}
                  <div className="flex-1 mb-3">
                    <p className={` ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} leading-relaxed`}>
                      {post.content}
                    </p>
                    
                    {/* Policy Tags */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {post.tags.map((tag, index) => {
                          const IconComponent = getPolicyTagIcon(tag);
                          const tagColor = getPolicyTagColor(tag, isDarkMode);
                          return (
                            <span 
                              key={index}
                              className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${tagColor} shadow-sm`}
                            >
                              <IconComponent className="w-3 h-3 mr-1" />
                              {tag}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Side: Image Preview */}
                {imageSrc && (
                  <div className="p-4 md:p-6 md:pl-0 flex items-start">
                    <img 
                      src={imageSrc} 
                      alt={post.content ? post.content.substring(0, 50) + '...' : 'Post image'}
                      className={`w-[200px] h-[200px] object-cover rounded-xl cursor-pointer shadow-md hover:scale-[1.02] hover:shadow-lg transition-all ${
                        isDarkMode ? 'border-[3px] border-gray-700' : 'border-[3px] border-gray-200'
                      }`}
                      onClick={() => setSelectedImage(imageSrc)}
                      loading="lazy"
                      onError={(e) => {
                        console.error('❌ Failed to load image:', imageSrc, 'Original:', originalImageUrl);
                        e.target.style.display = 'none';
                      }}
                      onLoad={() => {
                        console.log('✅ Image loaded successfully:', imageSrc);
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Bottom: Share Links for All Platforms */}
              <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} p-4 flex flex-wrap gap-2`}>
                {/* Share buttons for all platforms (main + equivalent) */}
                {allPlatforms.map((platformData, idx) => (
                  <a
                    key={idx}
                    href={platformData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-3 rounded-lg transition-all flex items-center justify-center group relative ${getPlatformShareColor(platformData.platform)}`}
                    title={`View on ${getPlatformDisplayName(platformData.platform)}`}
                  >
                    <ExternalLink className="w-6 h-6 group-hover:opacity-0 group-hover:scale-0 transition-all duration-200" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 scale-0 group-hover:scale-100 transition-all duration-200 pointer-events-none">
                      {getPlatformIcon(platformData.platform, "w-6 h-6", "text-white")}
                    </div>
                  </a>
                ))}
              </div>
            </div>
            );
          })
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999]"
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            width: '100vw', 
            height: '100vh',
            margin: 0,
            padding: '1rem'
          }}
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="relative flex items-center justify-center w-full h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={selectedImage} 
              alt="Enlarged post image"
              className="max-w-full max-h-full object-contain rounded-lg"
              style={{ maxWidth: '90vw', maxHeight: '90vh' }}
            />
            <button
              onClick={() => setSelectedImage(null)}
              className={`absolute top-6 right-6 p-3 rounded-full z-10 ${
                isDarkMode 
                  ? 'bg-gray-800 text-white hover:bg-gray-700' 
                  : 'bg-white text-gray-900 hover:bg-gray-100'
              } transition-colors shadow-lg`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialMediaPosts;
